import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import HeatmapPlot from './HeatmapPlot';
import { apiRequest } from '../../utils';
import './CompanyHeatmap.css';

/* The company's own culture heatmap — the same figures our own people read in the back office.

   ★★ This screen paints what it is handed and computes nothing. Both halves arrive already
   floored: a unit under a floor carries NO number, not a hidden one, and there are two different
   floors — answers for SAY, people for DO — so a unit can clear one and not the other. Any
   arithmetic added here would be arithmetic on figures somebody was deliberately not shown.

   ★★ The colour comes from the server too. The ramp's control points are the methodology's own
   cuts, so a copy of them in this file would start lying the day one is moved in the scoring
   model. A cell with no number gets no colour, because a colour here is a claim about a number.

   ★ Drilling into a unit re-reads the page with ?unit=; the server slices what it already read
   and every score stays measured against the WHOLE company, so "above the group" means the same
   sentence at every depth. */

const QUAD_KEY = {
  engaged: 'hmQuadEngaged',
  polite: 'hmQuadPolite',
  quiet: 'hmQuadQuiet',
  struggling: 'hmQuadStruggling',
};

/* ★ Two different nothings, and they must not look alike. A cell below a floor is a number that
   exists and is being withheld to protect the people in it; a cell with no score never had one.
   Telling a reader "too few to show" when the truth is "nobody answered" sends them looking for
   a privacy setting that is not the problem. */
/* ★ Deliberately colourless. The cells beside it already carry a colour scale that MEANS
   something, and a green/red chip on top puts two scales in one row fighting for the same eye —
   on a ramp that was chosen orange/teal precisely so it survives colour-blindness. The arrow
   carries the direction; the number carries the size. */
const Trend = ({ value, since }) => {
  const { t } = useTranslation();
  if (value === null || value === undefined) return null;
  if (Math.abs(value) < 0.05) {
    return <span className="hm-trend hm-trend--flat" title={since}>=</span>;
  }
  const up = value > 0;
  return (
    <span className={`hm-trend hm-trend--${up ? 'up' : 'down'}`}
          title={since ? t('hmSince', { date: since }) : undefined}>
      {up ? '▲' : '▼'} {Math.abs(value).toFixed(1)}
    </span>
  );
};

const Cell = ({ value, color, hidden, hiddenHint, trend, since }) => {
  const { t } = useTranslation();
  if (hidden) {
    return <td className="hm-cell hm-cell--held" title={hiddenHint}>·</td>;
  }
  if (value === null || value === undefined) {
    return <td className="hm-cell hm-cell--none" title={t('hmNoScore')}>—</td>;
  }
  return (
    <td className="hm-cell" style={{ backgroundColor: color || undefined }}>
      {Math.round(value)}
      <Trend value={trend} since={since} />
    </td>
  );
};

const CompanyHeatmap = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [season, setSeason] = useState('');
  const [unit, setUnit] = useState('');
  const [view, setView] = useState('table');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (season) qs.set('season', season);
      if (unit) qs.set('unit', unit);
      const q = qs.toString();
      setData(await apiRequest('GET', `/company/heatmap${q ? `?${q}` : ''}`));
    } catch {
      // Not a contact person of an engagement company — the same bounce every admin-only
      // screen in this track makes.
      navigate('/member-home');
    } finally {
      setLoading(false);
    }
  }, [season, unit, navigate]);

  useEffect(() => { load(); }, [load]);

  const rows = (data && data.rows) || [];
  const company = (data && data.company) || {};
  const trail = (data && data.trail) || [];
  const seasons = (data && data.seasons) || [];

  const quadLabel = (k) => t(QUAD_KEY[k]);
  const since = (data && data.baseline_on)
    ? new Date(`${data.baseline_on}T00:00:00`).toLocaleDateString()
    : null;
  const sayHint = t('hmHeldSay', { n: (data && data.say_threshold) || 0 });
  const doHint = t('hmHeldDo', { n: (data && data.do_threshold) || 0 });

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="chart-tree-map" theme="primary" className="me-2" />
          {t('hmTitle')}
        </Title>
        <p className="text-muted text-start hm-intro">{t('hmIntro')}</p>

        {seasons.length > 1 && (
          <div className="hm-seasons">
            <label htmlFor="hm-season">{t('hmSeason')}</label>
            <select
              id="hm-season"
              className="form-select form-select-sm"
              value={(data && data.season) || ''}
              onChange={(e) => { setUnit(''); setSeason(e.target.value); }}
            >
              {seasons.map((s) => (
                <option key={s.id} value={s.id}>{s.name || s.id}</option>
              ))}
            </select>
          </div>
        )}

        {/* ★ Before any behaviour is recorded every DO number is 0, so every unit lands in
            "says well, does little" — a confident picture of a month that has not happened.
            Say so rather than draw it. */}
        {data && !data.started && (
          <div className="hm-note hm-note--cold">
            <strong>{t('hmColdTitle')}</strong>
            <span>{t('hmColdBody')}</span>
          </div>
        )}

        {trail.length > 0 && (
          <nav className="hm-trail" aria-label={t('hmTitle')}>
            <button type="button" onClick={() => setUnit('')}>{t('hmAll')}</button>
            {trail.map((c, i) => (
              <React.Fragment key={c.id}>
                <span aria-hidden="true">›</span>
                {i === trail.length - 1
                  ? <span className="hm-trail__here">{c.name}</span>
                  : <button type="button" onClick={() => setUnit(c.id)}>{c.name}</button>}
              </React.Fragment>
            ))}
          </nav>
        )}

        {loading && <p className="text-muted">{t('loading')}</p>}

        {!loading && rows.length === 0 && (
          <div className="hm-note">
            <strong>{t('hmEmptyTitle')}</strong>
            <span>{t('hmEmptyBody')}</span>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <>
            {/* ★ A second way of looking, never a replacement: the table answers "what is this
                number" and the plot answers "where does this unit sit". Same fetch, same rows. */}
            <div className="hm-views" role="tablist">
              {['table', 'plot'].map((v) => (
                <button key={v} type="button" role="tab" aria-selected={view === v}
                        className={view === v ? 'is-on' : ''} onClick={() => setView(v)}>
                  {t(v === 'table' ? 'hmViewTable' : 'hmViewPlot')}
                </button>
              ))}
            </div>

            {view === 'plot' && (
              <HeatmapPlot rows={rows} company={company} quadrantLabel={quadLabel} />
            )}

            <div className="hm-scroll" hidden={view !== 'table'}>
              <table className="hm-table">
                <thead>
                  <tr>
                    <th>{t('hmUnit')}</th>
                    <th className="hm-num">{t('hmPeople')}</th>
                    <th className="hm-num">{t('hmSay')}</th>
                    <th className="hm-num">{t('hmDo')}</th>
                    <th>{t('hmQuadrant')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hm-row hm-row--company">
                    <td>{t('hmCompany')}</td>
                    <td className="hm-num">{company.headcount || 0}</td>
                    <Cell value={company.say} color={null} />
                    <Cell value={company.do} color={null} />
                    <td />
                  </tr>
                  {rows.map((r) => (
                    <tr key={r.id} className="hm-row">
                      <td>
                        <span
                          className={`hm-unit${r.has_children ? ' hm-unit--open' : ''}`}
                          style={{ paddingLeft: `${r.depth * 14}px` }}
                        >
                          {r.has_children ? (
                            <button type="button" onClick={() => setUnit(r.id)}>{r.name}</button>
                          ) : r.name}
                        </span>
                      </td>
                      <td className="hm-num">{r.headcount || 0}</td>
                      <Cell value={r.say} color={r.say_color} trend={r.say_trend} since={since}
                            hidden={r.say_hidden} hiddenHint={sayHint} />
                      <Cell value={r.do} color={r.do_color} trend={r.do_trend} since={since}
                            hidden={r.do_hidden} hiddenHint={doHint} />
                      <td className="hm-quad">
                        {r.quadrant ? quadLabel(r.quadrant) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="hm-legend">
              <span>{t('hmLegendLow')}</span>
              <i style={{ background: (data && data.ramp) || undefined }} />
              <span>{t('hmLegendHigh')}</span>
            </div>

            <p className="hm-foot text-muted">
              {t('hmFloors', {
                say: (data && data.say_threshold) || 0,
                people: (data && data.do_threshold) || 0,
              })}
              {data && data.unplaced ? ` ${t('hmUnplaced', { n: data.unplaced })}` : ''}
            </p>
          </>
        )}
      </Card>
    </Base>
  );
};

export default CompanyHeatmap;
